import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { X, User, Info, CheckCircle2, Save } from 'lucide-react';

interface UserProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PROFILE_KEY = 'rb_user_profile';

function loadProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveProfile(data: Record<string, string | undefined>) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(data));
}

export default function UserProfileDialog({ isOpen, onClose }: UserProfileDialogProps) {
  const { user } = useAuth();
  const profile = loadProfile();

  const [firstName, setFirstName] = useState(profile.firstName || '');
  const [lastName, setLastName] = useState(profile.lastName || '');
  const [name, setName] = useState(user?.name || profile.name || '');
  const [saved, setSaved] = useState(false);

  if (!isOpen || !user) return null;

  const handleSave = () => {
    saveProfile({
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      name: name.trim() || undefined,
    });
    // Also update the user's display name in the auth store
    const storageKey = 'rb_mock_auth';
    try {
      const session = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (session.user) {
        session.user.name = name.trim() || user.username;
        session.user.firstName = firstName.trim() || undefined;
        session.user.lastName = lastName.trim() || undefined;
        localStorage.setItem(storageKey, JSON.stringify(session));
      }
    } catch { /* ignore */ }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-rb-surface/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-lg shadow-[0_24px_80px_rgba(10,22,40,0.3)] animate-in zoom-in-95 duration-300 rb-page-card">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rb-active flex items-center justify-center shadow-sm">
              <User className="w-5 h-5 text-rb-brand" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-200">User Profile</h2>
              <p className="text-sm text-slate-500">Optional details for report generation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-rb-elevated hover:bg-rb-elevated flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Note */}
          <div className="p-4 rounded-xl bg-rb-active/[0.04] border border-rb-active/10 flex items-start gap-3">
            <Info className="w-4 h-4 text-rb-active shrink-0 mt-0.5" />
            <p className="text-sm text-rb-active">
              <strong>Optional:</strong> Adding your name is not mandatory. Your first and last name will appear on generated A4 print reports. You can leave these blank and reports will be generated without a named author.
            </p>
          </div>

          {/* Username (read-only) */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Username
            </label>
            <input
              type="text"
              value={user.username || ''}
              disabled
              className="w-full px-4 py-2.5 text-sm text-slate-500 cursor-not-allowed rb-inner-card"
            />
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Display Name <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g., John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-800/60 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-rb-active/20 focus:border-rb-active shadow-[0_1px_4px_rgba(10,22,40,0.04)]"
            />
          </div>

          {/* First Name */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              First Name <span className="text-slate-400 font-normal normal-case">(optional, for reports)</span>
            </label>
            <input
              type="text"
              placeholder="e.g., John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-800/60 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-rb-active/20 focus:border-rb-active shadow-[0_1px_4px_rgba(10,22,40,0.04)]"
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Last Name <span className="text-slate-400 font-normal normal-case">(optional, for reports)</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-800/60 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-rb-active/20 focus:border-rb-active shadow-[0_1px_4px_rgba(10,22,40,0.04)]"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-2xl border-2 border-slate-800/60 text-sm font-bold text-slate-600 hover:bg-rb-elevated transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={updateProfile.isPending}
              className="px-6 py-2.5 rounded-2xl bg-rb-brand text-slate-200 text-sm font-bold hover:bg-[#b8931f] transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {saved ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
